import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import BaseModal from 'components/Modals/BaseModal';
import { usePerms } from 'contexts/PermsContext';
import ExportModal from 'features/Integrations/features/ConnectedDevices/features/DeviceList/DeviceModals/components/ExportModal/ExportModal';
import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import { canViewDeviceType } from 'features/Integrations/utils/integrationVisibility.utils';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const STATUS_MESSAGE_TIMEOUT_MS = 2500;

/** Sentinels the Time Period control has always used. */
const TIME_PERIOD_PLACEHOLDER = -1;
const TIME_PERIOD_CUSTOM = 0;

// Values are minutes; the placeholder and Custom rows keep their sentinel values.
const TIME_PERIOD_OPTIONS = [
  { value: TIME_PERIOD_PLACEHOLDER, label: 'Time Period' },
  { value: TIME_PERIOD_CUSTOM, label: 'Custom' },
  { value: 1, label: '1 Minute' },
  { value: 2, label: '2 Minutes' },
  { value: 5, label: '5 Minutes' },
  { value: 10, label: '10 Minutes' },
  { value: 15, label: '15 Minutes' },
  { value: 30, label: '30 Minutes' },
  { value: 60, label: '1 Hour' },
  { value: 120, label: '2 Hours' },
  { value: 180, label: '3 Hours' },
  { value: 240, label: '4 Hours' },
  { value: 300, label: '5 Hours' },
  { value: 600, label: '10 Hours' },
  { value: 720, label: '12 Hours' },
  { value: 1440, label: '1 Day' },
  { value: 10080, label: '7 Days' },
  { value: 43200, label: '30 Days' },
];

const DATA_AGGREGATION_OPTIONS = [{ value: 0, label: 'Data Aggregation' }];

export default function DeviceQueryControl({
  __setStartTime = () => {},
  __setEndTime = () => {},
  setShowCurrentDevice = () => {},
}) {
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const perms = usePerms();
  // Default to 1 day (minutes)
  const [timeMultiplier, setTimeMultiplier] = useState(1440);
  const [startTime, _setStartTime] = useState(new Date().getTime() - 24 * 60 * 60 * 1000);
  const [endTime, _setEndTime] = useState(new Date().getTime());
  // This control no longer fetches, so it has nothing to report but the one thing it
  // did do: the range was applied. Loading and failure belong to the consumers that
  // fetch on the range (the graph's overlay, the datasheet's error state), which is
  // why the old 'updating'/'failed' states went with the fetch (Mike, 2026-09-04).
  const [rangeApplied, setRangeApplied] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAdvancedDate, setShowAdvancedDate] = useState(false);
  const [customTimeInputs, setCustomTimeImputs] = useState({});

  useEffect(() => {
    if (!rangeApplied) return undefined;

    const timeoutId = window.setTimeout(() => {
      setRangeApplied(false);
    }, STATUS_MESSAGE_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [rangeApplied]);

  const setStartTime = (value) => {
    __setStartTime(value);
    _setStartTime(value);
  };

  const setEndTime = (value) => {
    __setEndTime(value);
    _setEndTime(value);
  };

  const formatDateTimeLocal = (date) => {
    const d = new Date(date);

    const pad = (num) => String(num).padStart(2, '0');

    const formattedDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

    return formattedDate;
  };

  const markTimeRangeDirty = () => {
    if (rangeApplied) {
      setRangeApplied(false);
    }
  };

  // This control only SETS the range. The graph fetches the series for the devices
  // it shows when the range it receives changes, and the datasheet does the same
  // for its one device — each names its devices and pages. Before this, Search
  // fetched every device in the workplace with no ids (Mike, 2026-09-04).
  const handleSubmit = (nextStartTime = startTime, nextEndTime = endTime) => {
    setStartTime(nextStartTime);
    setEndTime(nextEndTime);
    setShowCurrentDevice(false);
    setRangeApplied(true);
  };

  return (
    <div className="container-fluid p-0">
      {canViewDeviceType({ system_type: 'Flood' }, perms, visibleDeviceTypes) && (
        <div className="d-flex flex-column gap-2 bg-primary-shadow border border-theme-strong rounded-2 p-2 w-100">
          <div className="d-flex justify-content-between gap-2">
            <BaseSelect
              wrapperClassName="flex-fill"
              isSearchable
              selectProps={{
                'aria-label': 'Time Period',
                options: TIME_PERIOD_OPTIONS,
                value: findSelectOption(TIME_PERIOD_OPTIONS, timeMultiplier),
                onChange: (option) => {
                  markTimeRangeDirty();
                  // -1 is the placeholder row; picking it changes nothing.
                  const next = option?.value ?? TIME_PERIOD_PLACEHOLDER;
                  if (next === TIME_PERIOD_PLACEHOLDER) return;

                  setTimeMultiplier(next);

                  // 0 is "Custom": it hands the range over to the advanced date UI.
                  if (next === TIME_PERIOD_CUSTOM) {
                    setShowAdvancedDate(true);
                    return;
                  }

                  setShowAdvancedDate(false);
                  setStartTime(Date.now() - next * 60000);
                  setEndTime(Date.now());
                },
              }}
            />
            <BaseSelect
              wrapperClassName="flex-fill"
              isSearchable={false}
              selectProps={{
                'aria-label': 'Data Aggregation',
                options: DATA_AGGREGATION_OPTIONS,
                value: DATA_AGGREGATION_OPTIONS[0],
                // Stub control: no handler yet, matching the native select it replaces.
                isDisabled: true,
              }}
            />
          </div>

          <BaseModal
            show={showExportModal}
            onHide={() => setShowExportModal(false)}
            title="Export Device Data"
          >
            <ExportModal
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              formatDateTimeLocal={formatDateTimeLocal}
              setShowExportModal={setShowExportModal}
            />
          </BaseModal>
          {showAdvancedDate && (
            <div className="d-flex flex-column">
              <label className="text-center">Time Period</label>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-baseline">
                <label className="flex-fill flex-basis-0">Date From</label>
                <input
                  className="form-control flex-fill flex-basis-0"
                  type="datetime-local"
                  value={customTimeInputs.start || formatDateTimeLocal(Date.now())}
                  onChange={(e) => {
                    markTimeRangeDirty();
                    setCustomTimeImputs({
                      ...customTimeInputs,
                      start: e.target.value,
                    });

                    setStartTime(new Date(e.target.value).getTime());
                  }}
                />
              </div>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-baseline">
                <label className="flex-fill flex-basis-0">Date to</label>
                <input
                  className="form-control flex-fill flex-basis-0"
                  type="datetime-local"
                  value={customTimeInputs.end || formatDateTimeLocal(Date.now())}
                  onChange={(e) => {
                    markTimeRangeDirty();
                    setCustomTimeImputs({
                      ...customTimeInputs,
                      end: e.target.value,
                    });

                    setEndTime(new Date(e.target.value).getTime());
                  }}
                />
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between">
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowExportModal(true);
              }}
            >
              Export
            </button>
            <div className="d-flex align-items-center gap-2">
              {rangeApplied && (
                <div
                  className="d-flex align-items-center gap-2 small text-success"
                  role="status"
                  aria-live="polite"
                >
                  {/* Says only what this click did. It is not a fetch result: the data
                      for the new range lands in the graph and the datasheets, and each
                      announces its own loading and failure. */}
                  <span className="text-nowrap">Range applied</span>
                </div>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => {
                  // Reset to 1 day (our initial cached range)
                  markTimeRangeDirty();
                  setTimeMultiplier(1440);
                  setShowAdvancedDate(false);
                  // Update timeframe to 1 day immediately
                  const now = Date.now();
                  const nextStartTime = now - 1440 * 60000;
                  const nextEndTime = now;
                  handleSubmit(nextStartTime, nextEndTime);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={() => handleSubmit()}>
                Search
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
